/**
 * API Service with authentication
 * Handles all API calls with Clerk JWT tokens
 */

// API URL with proper fallback for production
// In development: uses VITE_API_URL env var or localhost
// In production: uses the deployed Worker URL
const API_URL = import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV
    ? 'http://localhost:8787'
    : 'https://api.jarix.io'  // 2026-06-19: cutover from *.workers.dev to api.jarix.io (WAF protected)
  );

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

// FI (Finanzas) module — type contract lives in fiTypes.ts, shared by
// api.ts, the worker's FI endpoints, and every FI component.
import type {
  FiOverviewResponse,
  VendorCostRate,
  CostType,
  OverheadCost,
  OverheadCategory,
  CostClass,
  AllocationMethod,
  GroupDomain,
  Deal,
  DealStage,
  DealType,
  DealsSummary,
  DealForecastPoint,
} from '../components/admin/fi/fiTypes';

/**
 * Get auth token from Clerk
 */
async function getAuthToken(): Promise<string | null> {
  try {
    // @ts-expect-error - Clerk is loaded globally
    const clerk = window.Clerk;
    if (!clerk?.session) return null;
    return await clerk.session.getToken();
  } catch {
    return null;
  }
}

/**
 * Make authenticated API request
 */
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    const token = await getAuthToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json() as T & { error?: string };

    if (!response.ok) {
      return {
        success: false,
        error: data.error || `HTTP ${response.status}`,
      };
    }

    return {
      success: true,
      data: data as T,
    };
  } catch (error) {
    console.error('API request error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error de conexión',
    };
  }
}

/**
 * API methods
 */
export const api = {
  // Auth
  async getMe() {
    return apiRequest<{ user: UserInfo }>('/api/auth/me');
  },

  // Admin - Tenants
  async listTenants() {
    return apiRequest<{ tenants: Tenant[] }>('/api/admin/tenants');
  },

  async createTenant(data: Partial<Tenant> & { secrets?: TenantSecrets }) {
    return apiRequest<{ id: string }>('/api/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateTenant(id: string, data: Partial<Tenant> & {
    secrets?: TenantSecrets;
    billingEmails?: string[];
    payment_terms?: string;
    billing_day?: number;
    proration_enabled?: boolean;
  }) {
    return apiRequest(`/api/admin/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteTenant(id: string) {
    return apiRequest(`/api/admin/tenants/${id}`, {
      method: 'DELETE',
    });
  },

  // Admin - Users (global)
  async listUsers() {
    return apiRequest<{ users: User[] }>('/api/admin/users');
  },

  async updateUser(id: string, data: { role?: string; tenantIds?: string[] }) {
    return apiRequest(`/api/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Admin - Admin Users (portal administrators)
  async listAdminUsers() {
    return apiRequest<{ adminUsers: AdminUser[] }>('/api/admin/admin-users');
  },

  async createAdminUser(data: { email: string; adminRole: AdminRole; sendWelcomeEmail?: boolean }) {
    return apiRequest<{ id: string }>('/api/admin/admin-users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateAdminUser(id: string, data: { adminRole: AdminRole }) {
    return apiRequest(`/api/admin/admin-users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteAdminUser(id: string) {
    return apiRequest(`/api/admin/admin-users/${id}`, {
      method: 'DELETE',
    });
  },

  // Admin - Tenant Users
  async listTenantUsers(tenantId: string) {
    return apiRequest<{ tenant: { id: string; name: string; slug: string }; users: TenantUser[] }>(
      `/api/admin/tenants/${tenantId}/users`
    );
  },

  async addTenantUser(tenantId: string, data: { email: string; role?: string; sendWelcomeEmail?: boolean }) {
    return apiRequest<{ userId: string }>(`/api/admin/tenants/${tenantId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateTenantUser(tenantId: string, userId: string, data: { role: string }) {
    return apiRequest(`/api/admin/tenants/${tenantId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async removeTenantUser(tenantId: string, userId: string) {
    return apiRequest(`/api/admin/tenants/${tenantId}/users/${userId}`, {
      method: 'DELETE',
    });
  },

  // Dashboard
  async getDashboard(timeRange: string, tenant?: string) {
    const params = new URLSearchParams({ timeRange });
    if (tenant) params.append('tenant', tenant);
    return apiRequest(`/api/dashboard?${params}`);
  },

  // Admin - Global Metrics
  async getGlobalMetrics() {
    return apiRequest<GlobalMetricsResponse>('/api/admin/metrics');
  },

  // Admin - Services Catalog
  async listServices(options?: { category?: string; active?: boolean }) {
    const params = new URLSearchParams();
    if (options?.category) params.append('category', options.category);
    if (options?.active !== undefined) params.append('active', options.active.toString());
    const query = params.toString();
    return apiRequest<{ services: Service[] }>(`/api/admin/services${query ? `?${query}` : ''}`);
  },

  async getService(id: string) {
    return apiRequest<{ service: Service }>(`/api/admin/services/${id}`);
  },

  async createService(data: Partial<Service>) {
    return apiRequest<{ id: string }>('/api/admin/services', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateService(id: string, data: Partial<Service>) {
    return apiRequest(`/api/admin/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteService(id: string) {
    return apiRequest(`/api/admin/services/${id}`, {
      method: 'DELETE',
    });
  },

  // Admin - Tenant Services
  async listTenantServices(tenantId: string) {
    return apiRequest<{ tenant: { id: string; name: string; slug: string }; services: TenantServiceWithDetails[] }>(
      `/api/admin/tenants/${tenantId}/services`
    );
  },

  async assignService(data: Partial<TenantService>) {
    return apiRequest<{ id: string }>('/api/admin/tenant-services', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateTenantService(id: string, data: Partial<TenantService>) {
    return apiRequest(`/api/admin/tenant-services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async removeTenantService(id: string) {
    return apiRequest(`/api/admin/tenant-services/${id}`, {
      method: 'DELETE',
    });
  },

  // Admin - Revenue Analytics
  async getRevenue(options?: { year?: number; month?: string }) {
    const params = new URLSearchParams();
    if (options?.year) params.append('year', options.year.toString());
    if (options?.month) params.append('month', options.month);
    const query = params.toString();
    return apiRequest<RevenueResponse>(`/api/admin/revenue${query ? `?${query}` : ''}`);
  },

  // Admin - Billing History (for charts)
  async getBillingHistory(options?: {
    startYear?: number;
    startMonth?: number;
    endYear?: number;
    endMonth?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.startYear) params.append('startYear', options.startYear.toString());
    if (options?.startMonth) params.append('startMonth', options.startMonth.toString());
    if (options?.endYear) params.append('endYear', options.endYear.toString());
    if (options?.endMonth) params.append('endMonth', options.endMonth.toString());
    const query = params.toString();
    return apiRequest<BillingHistoryResponse>(`/api/admin/billing/history${query ? `?${query}` : ''}`);
  },

  async createBillingSnapshot(data: { year: number; month: number; overwrite?: boolean }) {
    return apiRequest<BillingSnapshotResponse>('/api/admin/billing/snapshot', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Admin — Billing workflow endpoints
  // Returns per-period rows with full lifecycle state (invoice_status,
  // invoice_number, due_date, etc.) so the workflow UI can render
  // badges and action buttons without recomputing anything locally.
  /** Dry-run preview: simulates what the day-15 snapshot would produce
   *  right now for the given period and returns a per-tenant diff vs
   *  the previous period's saved snapshot. Does not persist anything. */
  async getBillingPreview(year: number, month: number) {
    return apiRequest<{
      data: {
        year: number; month: number;
        previousPeriod: { year: number; month: number; complete: boolean };
        rows: Array<{
          tenant_id: string; tenant_name: string; tenant_slug: string; primary_color: string;
          plan: string; price_per_user: number;
          previous_users: number | null; current_users: number;
          previous_total: number | null; current_total: number;
          delta_users: number | null; delta_total: number | null;
          prorated_count: number;
          proration_enabled: boolean;
          error?: string;
        }>;
        totals: { current: number; previous: number; delta: number; deltaPct: number | null };
        generatedAt: string;
      };
    }>(`/api/admin/billing/preview?year=${year}&month=${month}`);
  },

  // Live breakdown of a tenant's JumpCloud users grouped by email domain.
  // Used as invoice detail for multi-company orgs (e.g. Eron).
  async getUserDomainBreakdown(tenantSlug: string) {
    return apiRequest<{
      data: {
        tenant: { slug: string; name: string; plan: string | null; pricePerUser: number };
        domains: Array<{ domain: string; total: number; activated: number; staged: number; suspended: number }>;
        totals: { total: number; activated: number; staged: number; suspended: number };
        amounts: { all: number; activatedOnly: number };
        generatedAt: string;
      };
    }>(`/api/admin/billing/user-domains?tenant=${encodeURIComponent(tenantSlug)}`);
  },

  async getBillingPeriod(year: number, month: number) {
    return apiRequest<{
      data: {
        year: number;
        month: number;
        rows: Array<{
          id: string;
          tenant_id: string;
          tenant_name: string;
          tenant_slug: string;
          legal_name: string | null;
          tax_id: string | null;
          billing_emails: string | null;
          payment_terms: string;
          billable_users: number;
          plan: string;
          price_per_user: number;
          monthly_total: number;
          invoice_status: string;
          invoice_number: string | null;
          invoice_date: string | null;
          due_date: string | null;
          paid_date: string | null;
          sent_to_finance_at: string | null;
          purchase_order: string | null;
          notes_internal: string | null;
          invoice_pdf_url: string | null;
          primary_color: string;
        }>;
        total: number;
        byStatus: Record<string, { count: number; total: number }>;
      };
    }>(`/api/admin/billing/period?year=${year}&month=${month}`);
  },

  /** Fetch SaaS metrics derived from billing_history + tenants.
   *  Returns MRR, ARR, ARPU, NRR, LTV, churn, a 13-month series
   *  and a 3-month linear forecast. Read-only aggregate. */
  async getSaasMetrics() {
    return apiRequest<{
      data: {
        generatedAt: string;
        current: {
          period: string | null;
          mrr: number;
          arr: number;
          arpu: number | null;
          activeTenants: number;
          activeUsers: number;
        };
        growth: {
          mom: number | null;
          nrr: number | null;
          monthlyChurnRate: number | null;
          churnedTenants: number;
          cohortSize: number;
        };
        ltv: number | null;
        series: Array<{
          period: string;
          year: number;
          month: number;
          mrr: number;
          users: number;
          tenants: number;
        }>;
        forecast: Array<{ period: string; mrr: number }> | null;
      };
    }>('/api/admin/billing/saas-metrics');
  },

  /** Patch the lifecycle of a single billing row.
   *  Only include the fields you want to change. */
  async updateBillingRow(rowId: string, patch: {
    invoice_status?: 'pending' | 'sent_to_finance' | 'invoiced' | 'paid' | 'overdue' | 'void';
    invoice_number?: string | null;
    invoice_date?: string | null;
    due_date?: string | null;
    paid_date?: string | null;
    purchase_order?: string | null;
    notes_internal?: string | null;
    invoice_pdf_url?: string | null;
  }) {
    return apiRequest<{ data: unknown }>(
      `/api/admin/billing/row/${encodeURIComponent(rowId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(patch),
      },
    );
  },

  /** Send the month's billing snapshot to Finanzas as an email with
   *  the NetSuite CSV attached. Refuses if any tenant in the period
   *  is missing fiscal data (422 with missingTenants in the body). */
  async sendBillingToFinance(year: number, month: number, recipient?: string) {
    return apiRequest<{
      emailId?: string;
      resendMessageId?: string;
      recipient?: string;
      period?: string;
      tenantCount?: number;
      totalUsd?: number;
      missingTenants?: Array<{ slug: string; name: string }>;
    }>('/api/admin/billing/send-to-finance', {
      method: 'POST',
      body: JSON.stringify({ year, month, recipient }),
    });
  },

  // Admin - Combined Billing (managed + professional services)
  async getCombinedBilling(options?: {
    startYear?: number;
    startMonth?: number;
    endYear?: number;
    endMonth?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.startYear) params.append('startYear', options.startYear.toString());
    if (options?.startMonth) params.append('startMonth', options.startMonth.toString());
    if (options?.endYear) params.append('endYear', options.endYear.toString());
    if (options?.endMonth) params.append('endMonth', options.endMonth.toString());
    const query = params.toString();
    return apiRequest<CombinedBillingResponse>(`/api/admin/billing/combined${query ? `?${query}` : ''}`);
  },

  // Admin - Professional Services Clients
  async listClients(options?: { status?: string; type?: string; q?: string; owner?: string; includeDeleted?: boolean }) {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.type) params.append('type', options.type);
    if (options?.q) params.append('q', options.q);
    if (options?.owner) params.append('owner', options.owner);
    if (options?.includeDeleted) params.append('includeDeleted', 'true');
    const query = params.toString();
    return apiRequest<{ clients: Client[] }>(`/api/admin/clients${query ? `?${query}` : ''}`);
  },

  /** Jarix team users eligible as client owner. */
  async listTeamUsers() {
    return apiRequest<{ users: Array<{ id: string; name: string | null; email: string }> }>(
      '/api/admin/team-users'
    );
  },

  async getClient(id: string) {
    return apiRequest<{ client: Client }>(`/api/admin/clients/${id}`);
  },

  async createClient(data: ClientFormData) {
    return apiRequest<{ id: string }>('/api/admin/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateClient(id: string, data: Partial<ClientFormData>) {
    return apiRequest(`/api/admin/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteClient(id: string, opts?: { confirm?: boolean }) {
    // `?confirm=true` tells the worker "yes, I know historical rows
    // will also be soft-deleted" — see handleAdminDeleteClient.
    const query = opts?.confirm ? '?confirm=true' : ''
    return apiRequest<{
      requiresConfirmation?: boolean;
      historicalCount?: number;
      historicalSoftDeleted?: number;
      softDelete?: boolean;
    }>(`/api/admin/clients/${id}${query}`, {
      method: 'DELETE',
    });
  },

  /** Restore a soft-deleted client from the papelera. */
  async restoreClient(id: string) {
    return apiRequest<{ restored: boolean }>(`/api/admin/clients/${id}?restore=true`, {
      method: 'DELETE',
    });
  },

  // Admin - Client Services
  async listClientServices(clientId: string) {
    return apiRequest<{ client: { id: string; name: string }; services: ClientServiceWithDetails[] }>(
      `/api/admin/clients/${clientId}/services`
    );
  },

  async addClientService(clientId: string, data: ClientServiceFormData) {
    return apiRequest<{ id: string }>(`/api/admin/clients/${clientId}/services`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateClientService(id: string, data: Partial<ClientServiceFormData>) {
    return apiRequest(`/api/admin/client-services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async removeClientService(id: string) {
    return apiRequest(`/api/admin/client-services/${id}`, {
      method: 'DELETE',
    });
  },

  /** Bulk status update for multiple services of the same client. */
  async bulkUpdateClientServices(clientId: string, ids: string[], status: string, note?: string) {
    return apiRequest<{ updated: number }>(
      `/api/admin/clients/${clientId}/services/bulk`,
      { method: 'PATCH', body: JSON.stringify({ ids, status, note }) },
    );
  },

  /** Load the timeline for a client service. */
  async listClientServiceEvents(csId: string) {
    return apiRequest<{
      events: Array<{
        id: string;
        type: string;
        fromStatus: string | null;
        toStatus: string | null;
        note: string | null;
        actor: { id: string; name: string | null; email: string | null } | null;
        createdAt: string;
      }>
    }>(`/api/admin/client-services/${csId}/events`);
  },

  /** Append a free-form note / milestone / contact log entry. */
  async addClientServiceEvent(csId: string, payload: { type?: 'note' | 'milestone' | 'contact'; note: string }) {
    return apiRequest(`/api/admin/client-services/${csId}/events`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // ─── Sprint 7: Service Packages (SKU bundles) ────────────────────

  /** List available packages with their components. */
  async listPackages(options?: { active?: boolean }) {
    const q = options?.active ? '?active=true' : '';
    return apiRequest<{ packages: ServicePackage[] }>(`/api/admin/packages${q}`);
  },

  /** Create a new package. Components are optional and can be added later. */
  async createPackage(data: PackageFormData) {
    return apiRequest<{ id: string }>('/api/admin/packages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Update package metadata and (optionally) replace its components wholesale. */
  async updatePackage(id: string, data: Partial<PackageFormData>) {
    return apiRequest(`/api/admin/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deletePackage(id: string) {
    return apiRequest(`/api/admin/packages/${id}`, { method: 'DELETE' });
  },

  /** Apply a package to a client — expands into N client_services rows. */
  async applyPackage(clientId: string, data: {
    packageId: string;
    startDate: string;
    userCount?: number;
    notes?: string;
  }) {
    return apiRequest<{
      packageId: string;
      packageName: string;
      created: number;
      createdIds: string[];
      skipped: number;
      skippedServices: string[];
      appliedTier: PricingTier | null;
      userCount: number;
    }>(`/api/admin/clients/${clientId}/apply-package`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Recompose pending invoices for a period — rebuilds line_items
   *  from the current state of billing_history + client_services.
   *  Sprint 7.1 recovery tool. */
  async recomposeBillingPeriod(year: number, month: number) {
    return apiRequest<{
      period: { year: number; month: number };
      rebuilt: number;
      skipped: number;
      details: Array<{
        id: string; customer: string; status: string;
        oldTotal?: number; newTotal?: number;
      }>;
    }>('/api/admin/billing/recompose', {
      method: 'POST',
      body: JSON.stringify({ year, month }),
    });
  },

  // Enhanced Dashboard Endpoints

  async getAlerts(tenant: string, timeRange: string = '7d', options?: { severity?: string; status?: string }) {
    const params = new URLSearchParams({ tenant, timeRange });
    if (options?.severity) params.append('severity', options.severity);
    if (options?.status) params.append('status', options.status);
    return apiRequest<{ data: CrowdStrikeAlert[]; count: number }>(`/api/alerts?${params}`);
  },

  async getAlertsSummary(tenant: string) {
    return apiRequest<{ data: AlertsSummary }>(`/api/alerts/summary?tenant=${tenant}`);
  },

  async getDevice360(tenant: string, deviceId: string) {
    return apiRequest<{ data: Device360Data }>(`/api/device/360?tenant=${tenant}&deviceId=${deviceId}`);
  },

  /** Tenant-facing billing history. Returns invoices visible to the
   *  requesting user — enforced server-side by canAccessTenant. Strips
   *  internal-only fields (notes_internal, finance email metadata). */
  async getTenantBilling(tenant: string) {
    return apiRequest<{
      data: {
        tenant: {
          slug: string;
          name: string;
          legalName: string | null;
          taxId: string | null;
          country: string | null;
          paymentTerms: string | null;
        };
        summary: {
          totalPeriods: number;
          outstandingUsd: number;
          paidUsd: number;
          lastInvoiceDate: string | null;
          lastPaidDate: string | null;
        };
        rows: Array<{
          id: string;
          period_year: number;
          period_month: number;
          billable_users: number;
          plan: string;
          price_per_user: number;
          monthly_total: number;
          invoice_status: string;
          invoice_number: string | null;
          invoice_date: string | null;
          due_date: string | null;
          paid_date: string | null;
          purchase_order: string | null;
          snapshot_date: string;
        }>;
      };
    }>(`/api/tenant/billing?tenant=${encodeURIComponent(tenant)}`);
  },

  /** Read the tenant's display identity (name, domain, logo_url).
   *  Available to any tenant member. */
  async getTenantBranding(tenant: string) {
    return apiRequest<{
      data: { name: string; domain: string | null; logo_url: string | null };
    }>(`/api/tenant/branding?tenant=${encodeURIComponent(tenant)}`);
  },

  /** Self-service branding update — only tenant_users.role='admin' (or
   *  jarix_admin) can call this; the worker enforces it and returns 403
   *  otherwise. Pass `null` to clear the override and fall back to the
   *  derived favicon. */
  async updateTenantBranding(tenant: string, payload: { logo_url: string | null }) {
    return apiRequest<{
      data: { name: string; domain: string | null; logo_url: string | null };
    }>(`/api/tenant/branding?tenant=${encodeURIComponent(tenant)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  /** Upload a logo file (multipart/form-data). Same auth as
   *  updateTenantBranding. Returns the absolute logo_url that the
   *  worker stored — the frontend should treat it as the new persisted
   *  value (replaces whatever was there). The worker enforces:
   *    - max size 512 KB
   *    - mime ∈ { png, jpg, webp, svg, gif }
   *  We do NOT use apiRequest() because it serializes JSON; uploads
   *  need raw multipart, and we can't set Content-Type ourselves
   *  (the browser sets the boundary). */
  async uploadTenantLogo(tenant: string, file: File): Promise<{ success: boolean; data?: { logo_url: string }; error?: string }> {
    try {
      const token = await getAuthToken();
      const fd = new FormData();
      fd.append('logo', file, file.name);
      const res = await fetch(`${API_URL}/api/tenant/branding/upload?tenant=${encodeURIComponent(tenant)}`, {
        method: 'POST',
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      const json = await res.json() as { success: boolean; data?: { logo_url: string }; error?: string };
      if (!res.ok) {
        return { success: false, error: json.error || `HTTP ${res.status}` };
      }
      return { success: true, data: json.data };
    } catch (err) {
      console.error('uploadTenantLogo error:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Error de conexión' };
    }
  },

  async getUnifiedAlerts(tenant: string, timeRange: string = '7d') {
    return apiRequest<{
      data: import('../types').UnifiedAlert[];
      count: number;
      total: number;
      sources: { crowdstrike: number | null; jumpcloud: number | null };
    }>(`/api/alerts/unified?tenant=${encodeURIComponent(tenant)}&timeRange=${encodeURIComponent(timeRange)}`);
  },

  async getUserThreats(tenant: string, userId: string) {
    // encodeURIComponent guards against any stray chars in tenant/userId even though
    // the worker also validates the userId shape.
    return apiRequest<{ data: {
      user: { id: string; email: string; username: string };
      threats: Array<{
        userId: string; userName: string; userEmail: string;
        deviceId: string; deviceName: string; devicePlatform: string;
        detection: { id: string; type: string; severity: string; tactic: string; technique?: string; description?: string; timestamp: string };
        status: string;
      }>;
      devices: Array<{ device_id: string; hostname: string; platform_name: string; os_version: string; last_seen: string; status: string }>;
      threatCount: number;
      deviceCount: number;
    } }>(`/api/users/${encodeURIComponent(userId)}/threats?tenant=${encodeURIComponent(tenant)}`);
  },

  async executeUserAction(tenant: string, userId: string, action: 'unlock' | 'expire_password' | 'suspend', confirmEmail?: string) {
    return apiRequest<{ action: string; userId: string; auditLogId: string }>(
      `/api/users/${encodeURIComponent(userId)}/actions?tenant=${encodeURIComponent(tenant)}`,
      {
        method: 'POST',
        body: JSON.stringify({ action, confirmEmail }),
      }
    );
  },

  async getSystemInsightsDevice(tenant: string, systemId: string, tables?: string) {
    const params = new URLSearchParams({ tenant, systemId });
    if (tables) params.append('tables', tables);
    return apiRequest<{ data: DeviceSystemInsights }>(`/api/system-insights/device?${params}`);
  },

  async getFleetInsights(tenant: string) {
    return apiRequest<{ data: FleetInsightsSummary }>(`/api/system-insights/fleet?tenant=${tenant}`);
  },

  async getDevicesOnlineState(tenant: string) {
    return apiRequest<{ data: Record<string, string> }>(`/api/devices/online-state?tenant=${tenant}`);
  },

  async getMITREHeatmap(tenant: string) {
    return apiRequest<{ data: MITRETacticData[] }>(`/api/mitre/heatmap?tenant=${tenant}`);
  },

  async getSoftwareInventory(tenant: string, platform?: string) {
    const params = new URLSearchParams({ tenant });
    if (platform) params.append('platform', platform);
    return apiRequest(`/api/software/inventory?${params}`);
  },

  async getBrowserExtensions(tenant: string) {
    return apiRequest(`/api/extensions/audit?tenant=${tenant}`);
  },

  async getUSBDevices(tenant: string) {
    return apiRequest(`/api/usb/devices?tenant=${tenant}`);
  },

  async getPolicyCompliance(tenant: string) {
    return apiRequest(`/api/policies/compliance?tenant=${tenant}`);
  },

  async getAccessGraph(tenant: string) {
    return apiRequest(`/api/access/graph?tenant=${tenant}`);
  },

  async getThreatIntel(tenant: string) {
    return apiRequest(`/api/threat-intel?tenant=${tenant}`);
  },

  async getExposureAssets(tenant: string) {
    return apiRequest(`/api/exposure/assets?tenant=${tenant}`);
  },

  async getVulnerabilitiesBySeverity(tenant: string, severity: string) {
    return apiRequest(`/api/vulnerabilities/by-severity?tenant=${tenant}&severity=${severity}`);
  },

  async getSocRadarIncidents(tenant: string) {
    return apiRequest(`/api/socradar/incidents?tenant=${tenant}`);
  },

  async getSocRadarASM(tenant: string) {
    return apiRequest(`/api/socradar/asm?tenant=${tenant}`);
  },

  async getSocRadarDarkWeb(tenant: string) {
    return apiRequest(`/api/socradar/dark-web?tenant=${tenant}`);
  },

  async getCyberRiskData(tenant: string) {
    return apiRequest(`/api/supply-chain/data?tenant=${tenant}`);
  },

  async getSocRadarReport(tenant: string) {
    return apiRequest(`/api/socradar/report?tenant=${tenant}`);
  },

  async getClientBilling(clientId: string, year?: number, month?: number) {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (month) params.append('month', month.toString());
    const query = params.toString();
    return apiRequest<{ billing: ClientBillingSummary }>(`/api/admin/clients/${clientId}/billing${query ? `?${query}` : ''}`);
  },

  // ─── FI (Finanzas) module ─────────────────────────────────────────

  /** Owner cockpit: revenue/COGS/margin trend + splits + pipeline. */
  async getFiOverview(months: number = 12) {
    return apiRequest<{ data: FiOverviewResponse }>(`/api/admin/fi/overview?months=${months}`);
  },

  // Vendor cost rates ------------------------------------------------

  async listVendorCostRates() {
    return apiRequest<{ data: VendorCostRate[] }>('/api/admin/costs/vendor-rates');
  },

  async createVendorCostRate(data: {
    vendor: string;
    serviceId?: string | null;
    costType: CostType;
    unitCost: number;
    currency?: string;
    effectiveFrom: string;
    effectiveTo?: string | null;
    notes?: string | null;
  }) {
    return apiRequest<{ id: string }>('/api/admin/costs/vendor-rates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateVendorCostRate(id: string, data: Partial<{
    vendor: string;
    serviceId: string | null;
    costType: CostType;
    unitCost: number;
    currency: string;
    effectiveFrom: string;
    effectiveTo: string | null;
    notes: string | null;
  }>) {
    return apiRequest(`/api/admin/costs/vendor-rates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteVendorCostRate(id: string) {
    return apiRequest(`/api/admin/costs/vendor-rates/${id}`, { method: 'DELETE' });
  },

  // Overhead costs -----------------------------------------------------

  async listOverheadCosts() {
    return apiRequest<{ data: OverheadCost[] }>('/api/admin/costs/overhead');
  },

  async createOverheadCost(data: {
    name: string;
    category: OverheadCategory;
    costClass: CostClass;
    monthlyAmount: number;
    currency?: string;
    allocationMethod: AllocationMethod;
    effectiveFrom: string;
    effectiveTo?: string | null;
    isActive?: boolean;
    notes?: string | null;
  }) {
    return apiRequest<{ id: string }>('/api/admin/costs/overhead', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateOverheadCost(id: string, data: Partial<{
    name: string;
    category: OverheadCategory;
    costClass: CostClass;
    monthlyAmount: number;
    currency: string;
    allocationMethod: AllocationMethod;
    effectiveFrom: string;
    effectiveTo: string | null;
    isActive: boolean;
    notes: string | null;
  }>) {
    return apiRequest(`/api/admin/costs/overhead/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteOverheadCost(id: string) {
    return apiRequest(`/api/admin/costs/overhead/${id}`, { method: 'DELETE' });
  },

  // Group / intragroup domains ------------------------------------------

  async listGroupDomains() {
    return apiRequest<{ data: GroupDomain[] }>('/api/admin/fi/group-domains');
  },

  async updateGroupDomain(id: string, data: Partial<{
    emailDomain: string;
    subCompanyName: string;
    isIntragroup: boolean;
    notes: string | null;
  }>) {
    return apiRequest(`/api/admin/fi/group-domains/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /** Flip the is_intragroup / group_label flag on a tenant or client. */
  async setGroupFlag(data: {
    entityType: 'tenant' | 'client';
    id: string;
    isIntragroup: boolean;
    groupLabel?: string | null;
  }) {
    return apiRequest<{ data: { entityType: 'tenant' | 'client'; id: string; isIntragroup: boolean; groupLabel: string | null } }>(
      '/api/admin/fi/group-flag',
      { method: 'PATCH', body: JSON.stringify(data) },
    );
  },

  // Deals / pipeline -----------------------------------------------------

  async listDeals(filters?: {
    stage?: DealStage;
    dealType?: DealType;
    ownerUserId?: string;
    q?: string;
    includeDeleted?: boolean;
  }) {
    const params = new URLSearchParams();
    if (filters?.stage) params.append('stage', filters.stage);
    if (filters?.dealType) params.append('dealType', filters.dealType);
    if (filters?.ownerUserId) params.append('ownerId', filters.ownerUserId);
    if (filters?.q) params.append('q', filters.q);
    if (filters?.includeDeleted) params.append('includeDeleted', 'true');
    const query = params.toString();
    return apiRequest<{ data: Deal[] }>(`/api/admin/deals${query ? `?${query}` : ''}`);
  },

  async getDealsSummary() {
    return apiRequest<{ data: DealsSummary }>('/api/admin/deals/summary');
  },

  async getDealsForecast(months: number = 6) {
    return apiRequest<{ data: DealForecastPoint[] }>(`/api/admin/deals/forecast?months=${months}`);
  },

  async getDeal(id: string) {
    return apiRequest<{ data: Deal }>(`/api/admin/deals/${id}`);
  },

  async createDeal(data: {
    name: string;
    dealType: DealType;
    stage?: DealStage;
    projectedMrr?: number;
    projectedOneTime?: number;
    probability?: number;
    currency?: string;
    expectedCloseDate?: string | null;
    estimatedUsers?: number | null;
    plan?: string | null;
    packageId?: string | null;
    ownerUserId?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    source?: string | null;
    isIntragroup?: boolean;
    notes?: string | null;
  }) {
    return apiRequest<{ id: string }>('/api/admin/deals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateDeal(id: string, data: Partial<Omit<Deal, 'id' | 'createdAt' | 'updatedAt'>>) {
    return apiRequest(`/api/admin/deals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async updateDealStage(id: string, data: { stage: DealStage; lostReason?: string | null }) {
    return apiRequest(`/api/admin/deals/${id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /** Convert a won deal into a tenant (managed) or client (professional/hybrid). */
  async convertDeal(id: string) {
    return apiRequest<{ data: { tenantId: string | null; clientId: string | null } }>(
      `/api/admin/deals/${id}/convert`,
      { method: 'POST' },
    );
  },

  /** Soft-delete a deal. */
  async deleteDeal(id: string) {
    return apiRequest(`/api/admin/deals/${id}`, { method: 'DELETE' });
  },

  /** Restore a soft-deleted deal. */
  async restoreDeal(id: string) {
    return apiRequest(`/api/admin/deals/${id}/restore`, {
      method: 'POST',
    });
  },
};

// Types

// Admin roles and permissions
type AdminRole = 'super_admin' | 'admin' | 'viewer';

interface AdminPermissions {
  canAccessMetrics: boolean;
  canAccessClients: boolean;
  canAccessServices: boolean;
  canAccessBilling: boolean;
  canCreateUsers: boolean;
  canEditUsers: boolean;
  canDeleteUsers: boolean;
}

interface UserTenant {
  id: string;
  name: string;
  slug: string;
  primary_color: string;
  logo_url?: string;
  domain?: string;
  tenant_role: string;
  socradar_enabled?: number;
  socradar_company_id?: string;
}

interface UserInfo {
  id: string;
  email: string;
  role: string;
  tenantId?: string;
  adminRole?: AdminRole;
  permissions?: AdminPermissions;
  tenants?: UserTenant[];
}

interface AdminUser {
  id: string;
  admin_role: AdminRole;
  created_at: string;
  updated_at: string;
  user_id: string;
  email: string;
  name?: string;
  clerk_id: string;
  created_by_email?: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended' | 'trial';
  plan: 'free' | 'standard' | 'enterprise';
  crowdstrike_client_id?: string;
  crowdstrike_region: string;
  crowdstrike_tag?: string; // FalconGroupingTag to filter devices for this tenant
  jumpcloud_org_id?: string;
  primary_color: string;
  created_at: string;
  updated_at: string;
  // Billing fields
  legal_name?: string;
  tax_id?: string;
  billing_address?: string;
  billing_postal_code?: string;
  billing_country?: string;
  billing_emails?: string; // JSON string of email array
}

interface TenantSecrets {
  crowdstrike_client_secret?: string;
  jumpcloud_api_key?: string;
}

interface User {
  id: string;
  clerk_id: string;
  email: string;
  name?: string;
  role: string;
  tenants: { id: string; name: string; slug: string }[];
  created_at: string;
  updated_at: string;
}

interface TenantUser {
  id: string;
  clerk_id: string;
  email: string;
  name?: string;
  global_role: string;
  tenant_role: string;
  assigned_at: string;
}

// Global Metrics Types
interface TenantSecurityData {
  securityScore: number;
  totalDevices: number;
  protectedDevices: number;
  totalDetections: number;
  criticalDetections: number;
  totalUsers: number;
  activeUsers: number;
  totalJumpCloudDevices?: number;
}

interface TenantBillingData {
  legalName: string | null;
  taxId: string | null;
  billingAddress: string | null;
  billingPostalCode: string | null;
  billingCountry: string | null;
  billingEmails: string[];
  hasBillingInfo: boolean;
  billableUsers: number;
  monthlyTotal: number;
  /** Payment terms — one of net_15 / net_30 (default) / net_45 / net_60.
   *  Drives automatic due-date derivation on the invoice lifecycle. */
  paymentTerms?: 'net_15' | 'net_30' | 'net_45' | 'net_60';
  /** Day of the month used as the billing cutoff. Defaults to 15 globally. */
  billingDay?: number;
  /** When true, users created mid-month are billed proportionally. Off by default. */
  prorationEnabled?: boolean;
}

interface TenantSummary {
  id: string;
  name: string;
  slug: string;
  status: 'active' | 'suspended' | 'trial';
  plan: 'free' | 'standard' | 'enterprise';
  planDisplayName?: string;
  pricePerUser?: number;
  primaryColor: string;
  platformUserCount: number;
  createdAt: string;
  hasCredentials: boolean;
  billing?: TenantBillingData;
  securityData: TenantSecurityData | null;
  error: string | null;
}

interface GlobalMetrics {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  suspendedTenants: number;
  planDistribution: {
    essentials: { count: number; pricePerUser: number };
    shield: { count: number; pricePerUser: number };
    advance: { count: number; pricePerUser: number };
  };
  totalDevices: number;
  totalProtectedDevices: number;
  totalJumpCloudDevices?: number;
  totalBillableUsers: number;
  totalActiveUsers?: number;
  totalDetections: number;
  criticalDetections: number;
  averageSecurityScore: number;
  platformUsers: number;
  billing?: {
    period: string;
    cutoffDay: number;
    currency: string;
    totalMonthlyRevenue: number;
    tenantsWithCompleteBillingInfo: number;
    tenantsMissingBillingInfo: number;
  };
}

interface MetricsAlerts {
  criticalScore: TenantSummary[];
  highDetections: TenantSummary[];
  missingCredentials: TenantSummary[];
  missingBillingInfo?: TenantSummary[];
  fetchErrors: TenantSummary[];
}

interface GlobalMetricsResponse {
  success: boolean;
  globalMetrics: GlobalMetrics;
  tenantSummaries: TenantSummary[];
  alerts: MetricsAlerts;
  ranking: TenantSummary[];
  generatedAt: string;
}

// Service Catalog Types
type ServiceCategory = 'package' | 'implementation' | 'consulting' | 'offensive' | 'license' | 'automation';
type PricingType = 'per_user_monthly' | 'one_time' | 'hourly' | 'project' | 'license_monthly' | 'license_annual';
type TenantServiceStatus = 'quote' | 'pending' | 'active' | 'completed' | 'cancelled' | 'renewed';

interface Service {
  id: string;
  name: string;
  description?: string;
  category: ServiceCategory;
  pricing_type: PricingType;
  base_price: number;
  cost_price?: number;
  currency: string;
  vendor?: string;
  vendor_sku?: string;
  is_active: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface TenantService {
  id: string;
  tenant_id: string;
  service_id: string;
  status: TenantServiceStatus;
  start_date: string;
  end_date?: string;
  quantity: number;
  unit_price?: number;
  discount_percent: number;
  license_key?: string;
  renewal_date?: string;
  notes?: string;
  invoice_notes?: string;
  created_at: string;
  updated_at: string;
}

interface TenantServiceWithDetails extends TenantService {
  service_name: string;
  category: ServiceCategory;
  pricing_type: PricingType;
  base_price: number;
  vendor?: string;
}

interface RevenueResponse {
  success: boolean;
  summary: {
    totalRevenue: number;
    totalCost: number;
    totalMargin: number;
    marginPercent: number;
    activeServices: number;
    totalServices: number;
  };
  monthlyRevenue: Array<{
    month: string;
    revenue: number;
    cost: number;
    margin: number;
    serviceCount: number;
    byCategory: Record<string, number>;
  }>;
  categoryRevenue: Array<{
    category: string;
    revenue: number;
    percentage: number;
  }>;
  topTenants: Array<{
    id: string;
    name: string;
    revenue: number;
    serviceCount: number;
  }>;
  generatedAt: string;
}

// Billing History Types (for charts)
interface BillingHistoryResponse {
  success: boolean;
  data: Array<{
    period: string;        // "2026-01"
    periodLabel: string;   // "Ene 2026"
    total: number;
    byTenant: Record<string, number>;
  }>;
  tenants: Array<{
    slug: string;
    name: string;
    color: string;
  }>;
  totals: {
    allTime: number;
    currentMonth: number;
    previousMonth: number;
    growth: number;
  };
  generatedAt: string;
}

interface BillingSnapshotResponse {
  success: boolean;
  period: {
    year: number;
    month: number;
    label: string;
  };
  summary: {
    totalTenants: number;
    successCount: number;
    errorCount: number;
    totalBillableUsers: number;
    totalRevenue: number;
  };
  results: Array<{
    tenantSlug: string;
    tenantName: string;
    billableUsers: number;
    plan: string;
    pricePerUser: number;
    monthlyTotal: number;
    status: 'created' | 'updated' | 'error';
    error?: string;
  }>;
  generatedAt: string;
}

// Combined Billing Types (managed + professional services)
interface CombinedBillingPeriod {
  period: string;
  periodLabel: string;
  managedTotal: number;
  servicesTotal: number;
  total: number;
  totalUsers: number;
  byTenant: Record<string, number>;
  byTenantUsers: Record<string, number>;
  byClient: Record<string, number>;
}

interface CombinedBillingEntity {
  id: string;
  name: string;
  slug: string;
  type: 'managed' | 'professional';
  color: string;
  plan?: string;
  planDisplayName?: string;
  pricePerUser?: number;
  currentMrr: number;
  currentUsers?: number;
  mrrGrowth: number;
  userDelta?: number;
  history: Array<{
    period: string;
    managed: number;
    services: number;
    total: number;
    users: number;
  }>;
}

interface CombinedBillingSummary {
  mrr: number;
  arr: number;
  managedMrr: number;
  servicesMrr: number;
  totalUsers: number;
  userDelta: number;
  arpu: number;
  top1Share: number;
  top3Share: number;
}

interface CombinedBillingResponse {
  success: boolean;
  data: CombinedBillingPeriod[];
  tenants: Array<{ slug: string; name: string; color: string }>;
  clients: Array<{ slug: string; name: string; color: string }>;
  entities: CombinedBillingEntity[];
  summary: CombinedBillingSummary;
  totals: {
    allTime: number;
    currentMonth: number;
    previousMonth: number;
    growth: number;
    managedTotal: number;
    servicesTotal: number;
  };
  generatedAt: string;
}

// Professional Services Client Types
type ClientStatus = 'active' | 'inactive' | 'prospect';
type ClientType = 'professional' | 'hybrid';
type PaymentTerms = 'net_15' | 'net_30' | 'net_45' | 'net_60';
type ClientServiceStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
type BillingFrequency = 'one_time' | 'monthly' | 'quarterly' | 'annual';

interface Client {
  id: string;
  name: string;
  slug: string;
  linkedTenantId: string | null;
  linkedTenant: {
    id: string;
    name: string;
    slug: string;
    plan?: string | null;
    lastBillableUsers?: number | null;
    lastMonthlyTotal?: number | null;
    lastPeriod?: string | null;
  } | null;
  status: ClientStatus;
  clientType: ClientType;
  ownerUserId?: string | null;
  owner?: { id: string; name: string | null; email: string | null } | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  legalName: string | null;
  taxId: string | null;
  billingEmails: string[];
  address: string | null;
  paymentTerms: PaymentTerms;
  currency: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  activeServicesCount?: number;
}

export interface ClientFormData {
  name: string;
  slug: string;
  linkedTenantId?: string | null;
  status?: ClientStatus;
  clientType?: ClientType;
  ownerUserId?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  legalName?: string | null;
  taxId?: string | null;
  billingEmails?: string[];  // Array of emails
  address?: string | null;
  paymentTerms?: PaymentTerms;
  currency?: string;
  notes?: string | null;
}

interface ClientServiceWithDetails {
  id: string;
  clientId: string;
  serviceId: string;
  service: {
    id: string;
    name: string;
    category: string;
    pricingType: string;
    basePrice: number;
    costPrice?: number | null;
    vendor?: string;
  };
  status: ClientServiceStatus;
  startDate: string;
  endDate: string | null;
  executionDate: string | null;
  quantity: number;
  unitPrice: number | null;
  actualCost?: number | null;
  discountPercent: number;
  billingFrequency: BillingFrequency;
  estimatedHours: number | null;
  actualHours: number | null;
  deliverables: string[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  effectivePrice: number;
  totalPrice: number;
  // Margin fields added by backend (Sprint 13 / Apr 2026)
  effectiveCost?: number;
  totalCost?: number;
  margin?: number;
  marginPct?: number | null;
}

export interface ClientServiceFormData {
  serviceId: string;
  status?: ClientServiceStatus;
  startDate: string;
  endDate?: string | null;
  executionDate?: string | null;
  quantity?: number;
  unitPrice?: number | null;
  actualCost?: number | null;
  discountPercent?: number;
  billingFrequency?: BillingFrequency;
  estimatedHours?: number | null;
  actualHours?: number | null;
  notes?: string | null;
}

// ─── Sprint 7: Service Packages ──────────────────────────────────

export type PackagePriceModel = 'per_user' | 'fixed_monthly' | 'custom';

/** Sprint 7.1: volume-based pricing. A package can define tiers like
 *  1-10 users @ $40/user, 11-50 @ $35/user, 51+ @ $30/user.
 *  `max_users: null` on the top tier = no upper bound. */
export interface PricingTier {
  min_users: number;
  max_users: number | null;
  price_per_user: number;
}

export interface PackageComponent {
  id: string;
  packageId: string;
  serviceId: string;
  serviceName: string;
  serviceCategory: string | null;
  serviceBasePrice: number;
  serviceCostPrice: number | null;
  pricingType: string;
  quantity: number;
  overridePrice: number | null;
  discountPercent: number;
  billingFrequency: BillingFrequency;
  sortOrder: number;
}

export interface ServicePackage {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  priceModel: PackagePriceModel;
  displayPrice: number;
  currency: string;
  isActive: boolean;
  sortOrder: number;
  /** Optional volume tiers. Empty array when the package has flat pricing. */
  pricingTiers: PricingTier[];
  createdAt: string;
  updatedAt: string;
  components: PackageComponent[];
}

export interface PackageFormData {
  name: string;
  slug: string;
  description?: string | null;
  priceModel?: PackagePriceModel;
  displayPrice?: number;
  currency?: string;
  isActive?: boolean;
  sortOrder?: number;
  pricingTiers?: PricingTier[] | null;
  components?: Array<{
    serviceId: string;
    quantity?: number;
    overridePrice?: number | null;
    discountPercent?: number;
    billingFrequency?: BillingFrequency;
    sortOrder?: number;
  }>;
}

interface ClientBillingSummary {
  clientId: string;
  clientName: string;
  clientSlug: string;
  periodYear: number;
  periodMonth: number;
  servicesTotal: number;
  servicesCount: number;
  services: Array<{
    serviceId: string;
    serviceName: string;
    quantity: number;
    unitPrice: number;
    discount: number;
    total: number;
  }>;
}

// Dashboard Data Fetchers
import type {
  DashboardData,
  VulnerabilitySummary,
  MFAStatus,
  ShadowITApp,
  DeviceCompliance,
  ZTAScore,
  ScoreTimeline,
  CrowdStrikeAlert,
  AlertsSummary,
  Device360Data,
  FleetInsightsSummary,
  DeviceSystemInsights,
  MITRETacticData,
} from '../types'

export async function fetchDashboardData(tenantSlug: string, timeRange: string): Promise<DashboardData> {
  const response = await api.getDashboard(timeRange, tenantSlug)
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch dashboard data')
  }
  return response.data as unknown as DashboardData
}

interface V2DataResult {
  vulnerabilities: VulnerabilitySummary | null
  mfaStatus: MFAStatus | null
  shadowIT: ShadowITApp[]
  compliance: DeviceCompliance | null
  ztaScore: ZTAScore | null
  scoreTimeline: ScoreTimeline | null
  // New enhancement data
  alerts: CrowdStrikeAlert[]
  alertsSummary: AlertsSummary | null
  fleetInsights: FleetInsightsSummary | null
  mitreHeatmap: MITRETacticData[]
}

export async function fetchV2Data(tenantSlug: string, timeRange: string): Promise<V2DataResult> {
  const params = new URLSearchParams({ tenant: tenantSlug, timeRange })

  const endpoints = [
    { key: 'vulnerabilities', url: `/api/vulnerabilities/summary?${params}` },
    { key: 'mfaStatus', url: `/api/mfa/status?${params}` },
    { key: 'shadowIT', url: `/api/shadow-it?${params}` },
    { key: 'compliance', url: `/api/compliance/devices?${params}` },
    { key: 'ztaScore', url: `/api/zta/score?${params}` },
    { key: 'scoreTimeline', url: `/api/score/history?${params}` },
    // Enhancement endpoints
    { key: 'alerts', url: `/api/alerts?${params}` },
    { key: 'alertsSummary', url: `/api/alerts/summary?${params}` },
    { key: 'fleetInsights', url: `/api/system-insights/fleet?${params}` },
    { key: 'mitreHeatmap', url: `/api/mitre/heatmap?${params}` },
  ]

  const results: V2DataResult = {
    vulnerabilities: null,
    mfaStatus: null,
    shadowIT: [],
    compliance: null,
    ztaScore: null,
    scoreTimeline: null,
    alerts: [],
    alertsSummary: null,
    fleetInsights: null,
    mitreHeatmap: [],
  }

  // Fetch all V2 endpoints in parallel
  const responses = await Promise.allSettled(
    endpoints.map(async ({ key, url }) => {
      const response = await apiRequest(url)
      return { key, response }
    })
  )

  for (const result of responses) {
    if (result.status === 'fulfilled' && result.value.response.success) {
      const { key, response } = result.value
      const data = response.data as { data?: unknown }
      switch (key) {
        case 'vulnerabilities':
          results.vulnerabilities = (data?.data as VulnerabilitySummary) || null
          break
        case 'mfaStatus':
          results.mfaStatus = (data?.data as MFAStatus) || null
          break
        case 'shadowIT':
          results.shadowIT = (data?.data as ShadowITApp[]) || []
          break
        case 'compliance':
          results.compliance = (data?.data as DeviceCompliance) || null
          break
        case 'ztaScore':
          results.ztaScore = (data?.data as ZTAScore) || null
          break
        case 'scoreTimeline':
          results.scoreTimeline = (data?.data as ScoreTimeline) || null
          break
        case 'alerts':
          results.alerts = (data?.data as CrowdStrikeAlert[]) || []
          break
        case 'alertsSummary':
          results.alertsSummary = (data?.data as AlertsSummary) || null
          break
        case 'fleetInsights':
          results.fleetInsights = (data?.data as FleetInsightsSummary) || null
          break
        case 'mitreHeatmap':
          results.mitreHeatmap = (data?.data as MITRETacticData[]) || []
          break
      }
    }
  }

  return results
}

export type {
  Tenant,
  TenantSecrets,
  User,
  TenantUser,
  UserTenant,
  GlobalMetricsResponse,
  GlobalMetrics,
  TenantSummary,
  MetricsAlerts,
  TenantSecurityData,
  Service,
  ServiceCategory,
  PricingType,
  TenantService,
  TenantServiceStatus,
  TenantServiceWithDetails,
  RevenueResponse,
  AdminRole,
  AdminPermissions,
  UserInfo,
  AdminUser,
  CombinedBillingResponse,
  CombinedBillingPeriod,
  CombinedBillingEntity,
  CombinedBillingSummary,
};
